//* Importing libries from teamAPI
import React, { useEffect, useState, useCallback, useMemo } from "react";
//* Importing Icons from lucide-react
import {
  Search,
  CheckCircle,
  PlusCircle,
  AlertTriangle,
  Repeat,
  Users,
  Trash2,
  X,
} from "lucide-react";
//* Importing components from react-bootstrap
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  InputGroup,
  Button,
  Alert,
  Badge,
  Spinner,
  ListGroup,
  Modal,
} from "react-bootstrap";
//* Importing custom from react-bootstrap
import { useStudents } from "../../../hooks/students/useStudents";
import { useRegistrations } from "../../../hooks/registration/useRegistration";

//* Importing libries from lodash
var debounce = require("lodash.debounce");

type StudentListProps = {
  teamId?: string;
};

const StudentList: React.FC<StudentListProps> = ({ teamId }) => {
  const token = localStorage.getItem("token") || "";

  const {
    registrations,
    loading: registrationsLoading,
    error: registrationsError,
    refetch,
    createRegistration,
    deleteRegistration,
    setFilters,
  } = useRegistrations(token, teamId, teamId ? 9999 : undefined);

  useEffect(() => {
    setFilters({ teamId });
  }, [teamId, setFilters]);

  const {
    students,
    loading: studentsLoading,
    error: studentsError,
    refetch: refetchStudents,
    pagination,
    changePage,
    setSearch,
  } = useStudents(token);

  const [searchInputValue, setSearchInputValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [notification, setNotification] = useState<{
    type: "success" | "danger";
    message: string;
    position?: "top" | "list";
  } | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<any>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      const trimmedValue = value.trim();
      if (trimmedValue.length >= 3) {
        setSearch(trimmedValue);
      } else {
        setSearch("");
      }
      setIsSearching(false);
    }, 500),
    [setSearch]
  );

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
    setIsSearching(true);
    debouncedSearch(e.target.value);
  };

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const filteredRegisteredStudents = useMemo(() => {
    const lowercasedTerm = searchInputValue.toLowerCase();
    return registrations
      .filter(
        (reg: any) =>
          !searchInputValue.trim() ||
          reg.student.name.toLowerCase().includes(lowercasedTerm) ||
          reg.student.email.toLowerCase().includes(lowercasedTerm)
      )
      .map((reg: any) => reg.student);
  }, [registrations, searchInputValue]);

  const registeredStudentIds = useMemo(
    () => new Set(registrations.map((reg: any) => reg.studentId)),
    [registrations]
  );

  const availableStudents = useMemo(() => {
    const lowercasedTerm = searchInputValue.toLowerCase();
    return students.filter(
      (student: any) =>
        (!searchInputValue.trim() ||
          student.name.toLowerCase().includes(lowercasedTerm) ||
          student.email.toLowerCase().includes(lowercasedTerm)) &&
        !registeredStudentIds.has(student.id)
    );
  }, [students, registeredStudentIds, searchInputValue]);

  const handleAddStudent = useCallback(
    async (studentId: string) => {
      if (!teamId) {
        setNotification({
          type: "danger",
          message: "ID da turma não está disponível.",
          position: "list",
        });
        return;
      }
      try {
        await createRegistration({
          studentId: studentId,
          teamId: teamId,
          isActive: true,
        });
        setNotification({
          type: "success",
          message: "Aluno adicionado com sucesso!",
          position: "list",
        });
        await refetch();
      } catch (err: any) {
        setNotification({
          type: "danger",
          message: err.message || "Erro ao adicionar aluno. Tente novamente.",
          position: "list",
        });
      }
    },
    [teamId, createRegistration, refetch]
  );

  const handleDeleteClick = useCallback((student: any) => {
    setStudentToDelete(student);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!studentToDelete) return;
    try {
      const registration = registrations.find(
        (reg: any) => reg.student.id === studentToDelete.id
      );
      if (registration) {
        await deleteRegistration(registration.id);
        setNotification({
          type: "success",
          message: "Aluno removido com sucesso!",
          position: "list",
        });
        await refetch();
      }
    } catch (err: any) {
      setNotification({
        type: "danger",
        message: err.message || "Erro ao remover aluno. Tente novamente.",
        position: "list",
      });
    } finally {
      setShowDeleteModal(false);
      setStudentToDelete(null);
    }
  }, [studentToDelete, registrations, deleteRegistration, refetch]);

  const StudentCard = React.memo(
    ({ student, isRegistered }: { student: any; isRegistered: boolean }) => (
      <ListGroup.Item className="d-flex justify-content-between align-items-center py-3">
        <div>
          <div className="fw-bold">{student.name}</div>
          <div className="text-muted small">{student.email}</div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {isRegistered ? (
            <>
              <Badge bg="success" className="d-flex align-items-center gap-2">
                <CheckCircle size={14} />
                Registrado
              </Badge>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDeleteClick(student)}
                className="ms-2"
              >
                <Trash2 size={14} />
              </Button>
            </>
          ) : (
            <Button
              variant="success"
              size="sm"
              onClick={() => handleAddStudent(student.id)}
              disabled={!teamId}
              className="d-flex align-items-center gap-2"
            >
              <PlusCircle size={14} />
              Adicionar
            </Button>
          )}
        </div>
      </ListGroup.Item>
    )
  );

  const NotificationAlert = ({ notification }: { notification: any }) => (
    <Alert
      variant={notification.type}
      className="d-flex align-items-center justify-content-between mb-3 fade show"
    >
      <div className="d-flex align-items-center gap-2">
        {notification.type === "success" ? (
          <CheckCircle size={18} />
        ) : (
          <AlertTriangle size={18} />
        )}
        {notification.message}
      </div>
      <Button
        variant="link"
        className="p-0 text-decoration-none text-dark"
        onClick={() => setNotification(null)}
      >
        <X size={18} />
      </Button>
    </Alert>
  );

  return (
    <>
      <Container className="py-4" data-testId="student-list">
        {notification && notification.position === "top" && (
          <div
            style={{
              position: "fixed",
              top: "1rem",
              right: "1rem",
              zIndex: 1050,
              minWidth: "300px",
              maxWidth: "500px",
            }}
          >
            <NotificationAlert notification={notification} />
          </div>
        )}

        <Card className="shadow-sm">
          <Card.Header className="bg-white">
            <div className="d-flex align-items-center gap-2">
              <Users className="text-primary" size={24} />
              <div>
                <h4 className="mb-0">Gerenciar Alunos</h4>
                <small className="text-muted">
                  Adicione ou remova alunos desta turma
                </small>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <Row className="mb-4">
              <Col>
                <InputGroup>
                  <InputGroup.Text>
                    <Search size={18} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Pesquisar por nome ou email..."
                    value={searchInputValue}
                    onChange={handleSearchInputChange}
                  />
                  {isSearching && (
                    <InputGroup.Text>
                      <Spinner animation="border" size="sm" />
                    </InputGroup.Text>
                  )}
                </InputGroup>
              </Col>
            </Row>
            {/* List-specific notifications */}
            {notification && notification.position === "list" && (
              <NotificationAlert notification={notification} />
            )}

            {/* Registered Students */}
            <div className="mb-4">
              <h5 className="mb-3 d-flex align-items-center gap-2">
                Alunos Registrados
                <Badge bg="primary" pill>
                  {filteredRegisteredStudents.length}
                </Badge>
              </h5>
              {registrationsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : registrationsError ? (
                <Alert variant="danger" className="mb-4">
                  <Alert.Heading className="h5 d-flex align-items-center gap-2">
                    <AlertTriangle size={18} />
                    Erro ao carregar alunos registrados
                  </Alert.Heading>
                  <p>{registrationsError}</p>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => refetch()}
                    >
                      <Repeat size={14} className="me-2" />
                      Tentar novamente
                    </Button>
                  </div>
                </Alert>
              ) : (
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <ListGroup>
                    {filteredRegisteredStudents.length > 0 ? (
                      filteredRegisteredStudents.map((student) => (
                        <StudentCard
                          key={student.id}
                          student={student}
                          isRegistered={true}
                        />
                      ))
                    ) : (
                      <ListGroup.Item className="text-center text-muted py-4">
                        Nenhum aluno registrado nesta turma
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </div>
              )}
            </div>

            {/* Available Students */}
            <div>
              <h5 className="mb-3 d-flex align-items-center gap-2">
                Alunos Disponíveis
                <Badge bg="secondary" pill>
                  {availableStudents.length}
                </Badge>
              </h5>
              {studentsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : studentsError ? (
                <Alert variant="danger" className="mb-4">
                  <Alert.Heading className="h5 d-flex align-items-center gap-2">
                    <AlertTriangle size={18} />
                    Erro ao carregar alunos disponíveis
                  </Alert.Heading>
                  <p>{studentsError}</p>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => refetchStudents()}
                    >
                      <Repeat size={14} className="me-2" />
                      Tentar novamente
                    </Button>
                  </div>
                </Alert>
              ) : (
                <>
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <ListGroup>
                      {availableStudents.length > 0 ? (
                        availableStudents.map((student) => (
                          <StudentCard
                            key={student.id}
                            student={student}
                            isRegistered={false}
                          />
                        ))
                      ) : (
                        <ListGroup.Item className="text-center text-muted py-4">
                          Todos os alunos já estão registrados
                        </ListGroup.Item>
                      )}
                    </ListGroup>
                  </div>

                  {/* PAGINAÇÃO SIMPLIFICADA */}
                  {pagination.totalPages > 1 && (
                    <>
                      {/* Linha com botões 'Anterior' / 'Próxima' */}
                      <div className="d-flex justify-content-center align-items-center mt-3">
                        <Button
                          variant="outline-primary"
                          onClick={() =>
                            changePage(Math.max(1, pagination.currentPage - 1))
                          }
                          disabled={pagination.currentPage === 1}
                        >
                          Anterior
                        </Button>
                        <span className="mx-3">
                          {pagination.currentPage} / {pagination.totalPages}
                        </span>
                        <Button
                          variant="outline-primary"
                          onClick={() =>
                            changePage(
                              Math.min(
                                pagination.totalPages,
                                pagination.currentPage + 1
                              )
                            )
                          }
                          disabled={
                            pagination.currentPage === pagination.totalPages
                          }
                        >
                          Próxima
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Remoção</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Tem certeza que deseja remover o aluno{" "}
          <strong>{studentToDelete?.name}</strong> desta turma?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Remover
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StudentList;
